import { useCallback, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronsLeft, ChevronsRight, Circle, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { ProductBrand } from '@barahpay/shared';
import { useAuth } from '../auth/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import { NAV_ITEMS } from './navigation';
import type { NavItem } from './navigation';
import { NAV_GROUPS, NAV_ICONS, activeNavItem } from './navIcons';

interface NavSection {
  group: NavItem['group'];
  labelKey: string;
  icon: LucideIcon;
  items: NavItem[];
}

/** Only the sections the user may open are listed; the backend re-checks each request (spec section 65). */
function useNavSections(): NavSection[] {
  const { canAny } = useAuth();

  return useMemo(
    () =>
      NAV_GROUPS.map((meta) => ({
        ...meta,
        items: NAV_ITEMS.filter((item) => item.group === meta.group && canAny(item.permissions)),
      })).filter((section) => section.items.length > 0),
    [canAny],
  );
}

function iconFor(item: NavItem): LucideIcon {
  return NAV_ICONS[item.icon] ?? Circle;
}

/**
 * The Barah Plus sidebar: a floating rounded card with the product lockup, a "MENU" caption,
 * accordion sections with a hairline rail, and a tinted pill for the current screen. Collapsed on a
 * wide screen it becomes an icon rail whose sections open as flyouts.
 */
export function Sidebar({
  mobile,
  collapsed,
  onToggleCollapsed,
  onClose,
}: {
  mobile: boolean;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { rtl } = useLanguage();
  const location = useLocation();
  const sections = useNavSections();
  const activePath = activeNavItem(location.pathname)?.path;
  const rail = collapsed && !mobile;

  const activeGroup =
    sections.find((section) => section.items.some((item) => item.path === activePath))?.group ?? null;

  // One section open at a time, and the one owning the route opens by itself. Adjusted during render
  // rather than in an effect, so navigating never paints the old section first.
  const [expanded, setExpanded] = useState<string | null>(activeGroup);
  const [lastActiveGroup, setLastActiveGroup] = useState(activeGroup);

  if (activeGroup !== lastActiveGroup) {
    setLastActiveGroup(activeGroup);

    if (activeGroup) {
      setExpanded(activeGroup);
    }
  }

  const onNavigate = mobile ? onClose : undefined;
  const CollapseIcon = !rail !== rtl ? ChevronsLeft : ChevronsRight;

  return (
    <div className="bp-sidebar__panel">
      <div className={`bp-sidebar__head${rail ? ' is-rail' : ''}`}>
        {rail ? null : <ProductBrand size="md" className="bp-sidebar__brand" />}

        {mobile ? (
          <button type="button" className="bp-chrome-btn" onClick={onClose} aria-label={t('common.closeMenu')}>
            <X size={16} />
          </button>
        ) : (
          <button
            type="button"
            className="bp-chrome-btn"
            onClick={onToggleCollapsed}
            aria-label={t('common.collapseMenu')}
            title={t('common.collapseMenu')}
          >
            <CollapseIcon size={18} />
          </button>
        )}
      </div>

      {rail ? null : <div className="bp-sidebar__caption">{t('common.menu')}</div>}

      <nav className={`bp-sidebar__nav${rail ? ' is-rail' : ''}`} aria-label={t('common.menu')}>
        {sections.map((section) =>
          rail ? (
            <RailSection key={section.group} section={section} activePath={activePath} rtl={rtl} />
          ) : (
            <MenuSection
              key={section.group}
              section={section}
              open={expanded === section.group}
              onToggle={() => setExpanded((current) => (current === section.group ? null : section.group))}
              activePath={activePath}
              onNavigate={onNavigate}
            />
          ),
        )}
      </nav>

      {rail ? null : <div className="bp-sidebar__foot">{t('common.poweredBy')}</div>}
    </div>
  );
}

function MenuSection({
  section,
  open,
  onToggle,
  activePath,
  onNavigate,
}: {
  section: NavSection;
  open: boolean;
  onToggle: () => void;
  activePath: string | undefined;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const hasActive = section.items.some((item) => item.path === activePath);
  const SectionIcon = section.icon;

  return (
    <div>
      <button
        type="button"
        className={`bp-nav-section${hasActive ? ' has-active' : ''}`}
        onClick={onToggle}
        aria-expanded={open}
      >
        <SectionIcon className="bp-nav-section__icon" aria-hidden />
        <span className="bp-nav-section__label">{t(section.labelKey)}</span>
        <ChevronDown className={`bp-nav-section__chevron${open ? ' is-open' : ''}`} aria-hidden />
      </button>

      {open ? (
        <div className="bp-nav-children">
          {section.items.map((item) => {
            const ItemIcon = iconFor(item);
            const active = item.path === activePath;

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onNavigate}
                className={`bp-nav-item${active ? ' is-active' : ''}`}
                aria-current={active ? 'page' : undefined}
              >
                <ItemIcon className="bp-nav-item__icon" aria-hidden />
                <span className="bp-nav-item__label">{t(`nav.${item.labelKey}`)}</span>
              </Link>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

/**
 * A section on the collapsed rail: the icon alone, and its entries in a flyout beside it.
 *
 * The flyout is positioned `fixed` against the window rather than inside the menu, because the menu
 * scrolls and `overflow` would clip a panel that by definition sits outside the rail. Its wrapper
 * starts flush against the rail and pads the gap, so the pointer never crosses dead space on its
 * way from the icon to the entries.
 */
function RailSection({
  section,
  activePath,
  rtl,
}: {
  section: NavSection;
  activePath: string | undefined;
  rtl: boolean;
}) {
  const { t } = useTranslation();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties | null>(null);
  const hasActive = section.items.some((item) => item.path === activePath);
  const SectionIcon = section.icon;

  const open = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();

    if (!rect) {
      return;
    }

    const top = Math.max(12, Math.min(rect.top, window.innerHeight - 64 - section.items.length * 40));

    setStyle({
      top,
      maxHeight: window.innerHeight - top - 12,
      ...(rtl ? { right: window.innerWidth - rect.left } : { left: rect.right }),
    });
  }, [rtl, section.items.length]);

  const close = useCallback(() => setStyle(null), []);

  return (
    <div ref={anchorRef} onMouseEnter={open} onMouseLeave={close} onFocus={open} onBlur={close}>
      <button
        type="button"
        className={`bp-nav-rail${hasActive ? ' is-active' : ''}`}
        aria-label={t(section.labelKey)}
        aria-haspopup="menu"
        aria-expanded={style !== null}
        onClick={() => (style ? close() : open())}
      >
        <SectionIcon aria-hidden />
      </button>

      {style ? (
        <div className="bp-nav-flyout" style={style}>
          <div className="bp-nav-flyout__panel">
            <div className="bp-nav-flyout__title">{t(section.labelKey)}</div>
            {section.items.map((item) => {
              const ItemIcon = iconFor(item);
              const active = item.path === activePath;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={close}
                  className={`bp-nav-item${active ? ' is-active' : ''}`}
                  aria-current={active ? 'page' : undefined}
                >
                  <ItemIcon className="bp-nav-item__icon" aria-hidden />
                  <span className="bp-nav-item__label">{t(`nav.${item.labelKey}`)}</span>
                </Link>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
